import { supabase } from '../../../lib/supabase'
import { COLORS } from '../data/colors'
import { CATEGORIES } from '../data/categories'
import type {
  ColorfulRoomRow, ColorfulPlayerRow, ColorfulPickRow, ColorfulRoomMode,
} from '../../../lib/database.types'

/**
 * Thin wrappers around the colorful_* RPCs. Each one matches a function
 * in the SQL migration; the client is deliberately dumb — all state
 * transitions live on the server so the TV view + phone controllers
 * agree at every moment.
 */

/**
 * `isPrivate` defaults to true so any call site that doesn't ask for a
 * public room stays closed to the online queue — only the Create Room
 * screen's toggle passes false on purpose.
 */
export async function createColorfulRoom(
  mode: ColorfulRoomMode = 'online', isPrivate = true,
): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('create_colorful_room', {
    p_mode: mode, p_is_private: isPrivate,
  })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function joinColorfulRoomByCode(code: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('join_colorful_room_by_code', { p_code: code.toUpperCase() })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function leaveColorfulRoom(roomId: string): Promise<void> {
  const { error } = await supabase.rpc('leave_colorful_room', { p_room_id: roomId })
  if (error) throw error
}

export async function addColorfulAi(roomId: string, aiPlayerId: string): Promise<ColorfulPlayerRow> {
  const { data, error } = await supabase.rpc('add_colorful_ai', {
    p_room_id: roomId, p_ai_player_id: aiPlayerId,
  })
  if (error) throw error
  return data as ColorfulPlayerRow
}

export async function removeColorfulPlayer(playerId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_colorful_player', { p_player_id: playerId })
  if (error) throw error
}

/**
 * Rename the caller's seat in a Colorful room. Updates both
 * `profiles.display_name` (sticky for future rooms) and the seat's
 * snapshot `display_name` (so the current room's UI reflects it now).
 */
export async function renameColorfulSeat(
  roomId: string, newName: string,
): Promise<ColorfulPlayerRow> {
  // Direct call — the unbinding type-cast trick used to live here
  // and was throwing "undefined is not an object (evaluating
  // 'this.rest')" on newer @supabase/supabase-js. Calling rpc on
  // the supabase client preserves `this` and works for both
  // authenticated and anonymous (guest) sessions.
  const { data, error } = await supabase.rpc('rename_colorful_seat', {
    p_room_id: roomId, p_new_name: newName,
  })
  if (error) throw error
  return data as ColorfulPlayerRow
}

/** Host-only. Ships the full color + category pools so the server shuffles. */
export async function startColorful(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('start_colorful', {
    p_room_id: roomId,
    p_color_pool: COLORS.map(c => c.id),
    p_category_pool: CATEGORIES.map(c => c.id),
  })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function beginColorfulRound(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('begin_colorful_round', { p_room_id: roomId })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function drawColorfulCard(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('draw_colorful_card', { p_room_id: roomId })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function redrawColorfulCard(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('redraw_colorful_card', { p_room_id: roomId })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function confirmColorfulCard(roomId: string, subject: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('confirm_colorful_card', {
    p_room_id: roomId, p_subject: subject,
  })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function submitColorfulPick(roomId: string, colorId: string): Promise<void> {
  const { error } = await supabase.rpc('submit_colorful_pick', {
    p_room_id: roomId, p_color_id: colorId,
  })
  if (error) throw error
}

export async function submitColorfulPickAsAi(playerId: string, colorId: string): Promise<void> {
  const { error } = await supabase.rpc('submit_colorful_pick_as_ai', {
    p_player_id: playerId, p_color_id: colorId,
  })
  if (error) throw error
}

export async function revealColorfulRound(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('reveal_colorful_round', { p_room_id: roomId })
  if (error) throw error
  return data as ColorfulRoomRow
}

export async function advanceColorfulRound(roomId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await supabase.rpc('advance_colorful_round', { p_room_id: roomId })
  if (error) throw error
  return data as ColorfulRoomRow
}

/** Set the finale reveal step. The host drives it from their phone; every
 *  other device (players + the TV) follows via realtime. Cast through
 *  `any` — `set_colorful_reveal_index` isn't in the generated types yet. */
export async function setColorfulRevealIndex(roomId: string, index: number): Promise<void> {
  const { error } = await (supabase as any).rpc('set_colorful_reveal_index', {
    p_room_id: roomId, p_index: index,
  })
  if (error) throw error
}

/** Host-only. Removes a dropped player so picking can advance.
 *  Cascades delete the player's pick row; auto-reveal fires
 *  if the kick brings outstanding picks to 0. Cast through `any`
 *  because this RPC isn't in the generated types yet. */
export async function kickColorfulPlayer(playerId: string): Promise<ColorfulRoomRow> {
  const { data, error } = await (supabase as any).rpc('kick_colorful_player', { p_player_id: playerId })
  if (error) throw error
  return data as ColorfulRoomRow
}

// ── Reads (for initial snapshot / fallbacks) ──────────────────────────

export async function fetchColorfulRoomById(roomId: string): Promise<ColorfulRoomRow | null> {
  const { data, error } = await supabase
    .from('colorful_rooms').select('*').eq('id', roomId).maybeSingle()
  if (error) throw error
  return (data as ColorfulRoomRow | null) ?? null
}

export async function fetchColorfulRoomByCode(code: string): Promise<ColorfulRoomRow | null> {
  const { data, error } = await supabase
    .from('colorful_rooms').select('*').eq('code', code.toUpperCase()).maybeSingle()
  if (error) throw error
  return (data as ColorfulRoomRow | null) ?? null
}

export async function fetchColorfulPlayers(roomId: string): Promise<ColorfulPlayerRow[]> {
  const { data, error } = await supabase
    .from('colorful_players').select('*').eq('room_id', roomId).order('join_order')
  if (error) throw error
  return (data ?? []) as ColorfulPlayerRow[]
}

export async function fetchColorfulPicks(roomId: string, roundIndex?: number): Promise<ColorfulPickRow[]> {
  let q = supabase.from('colorful_picks').select('*').eq('room_id', roomId)
  if (typeof roundIndex === 'number') q = q.eq('round_index', roundIndex)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as ColorfulPickRow[]
}
